import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import React, { useMemo } from 'react';

import { MatrixDataRow } from './MatrixDataRow';
import { MatrixGroupRow } from './MatrixGroupRow';
import { MatrixHeaderRow } from './MatrixHeaderRow';
import { ServiceActivityMatrixProps } from './ServiceActivityMatrix.props';
import { STICKY_MATRIX_SX } from './ServiceActivityMatrix.styles';
import { useMatrixRovingFocus } from './useMatrixRovingFocus';
import { MatrixFocusCell } from '../../../@types/service-activity.type';
import {
  ACTIVITY_STATUS_COLUMNS,
  ACTIVITY_TABLE_ARIA_LABEL,
} from '../../../constants/service-activity.constant';

/** Label column + ten status columns + total. */
const TOTAL_COLUMNS = ACTIVITY_STATUS_COLUMNS.length + 2;

const groupBodyId = (groupKey: string): string =>
  `activity-matrix-group-${groupKey.replace(/\s+/g, '-')}`;

/**
 * The activity matrix — client × status counts, grouped and heat-encoded.
 *
 * Replaces the flat 40-row table. Rows roll up under their client, cells carry
 * an intensity proportional to their column, and four levels of the grid drill
 * through to a filtered job list.
 *
 * The `<table>` and its accessible name are load-bearing: `e2e/pages/HomePage.ts`
 * finds the dashboard with `getByRole('table', { name: 'Service Activity
 * Monitor' })`. The table shell therefore renders even when there is no data —
 * the empty state lives in a row inside it, never in place of it.
 */
export const ServiceActivityMatrix: React.FC<ServiceActivityMatrixProps> = ({
  model,
  sort,
  onSort,
  collapsedGroups,
  onToggleGroup,
  canDrillDown,
  onCellSelect,
  onRowSelect,
  onColumnSelect,
  onGroupSelect,
  emptyMessage = 'No service activity to show.',
}) => {
  // Flattened focus grid, visible rows only — collapsed groups contribute
  // nothing, so arrow keys never land on a hidden cell.
  const focusCells = useMemo<MatrixFocusCell[]>(() => {
    if (!canDrillDown) return [];
    const cells: MatrixFocusCell[] = [];
    for (const group of model.groups) {
      if (collapsedGroups.has(group.key)) continue;
      for (const row of group.rows) {
        cells.push({ rowKey: row.key, columnIndex: -1, interactive: true });
        ACTIVITY_STATUS_COLUMNS.forEach((column, index) => {
          cells.push({
            rowKey: row.key,
            columnIndex: index,
            interactive: row.counts[column.field] > 0,
          });
        });
      }
    }
    return cells;
  }, [model.groups, collapsedGroups, canDrillDown]);

  const { registerRef, isActive, handleKeyDown } = useMatrixRovingFocus(focusCells);

  return (
    // No maxHeight: the table runs its full length and the page scrolls, rather
    // than the matrix owning a second scrollbar inside the page.
    <TableContainer sx={{ border: 1, borderColor: 'v4.border', borderRadius: 2 }}>
      <Table size="small" aria-label={ACTIVITY_TABLE_ARIA_LABEL} sx={STICKY_MATRIX_SX}>
        <Box component="caption" sx={visuallyHidden}>
          Open jobs by client and job type across ten workflow statuses. Use the arrow keys to move
          between counts; press Enter to open the matching filtered job list.
        </Box>

        <MatrixHeaderRow
          sort={sort}
          onSort={onSort}
          onColumnSelect={onColumnSelect}
          canDrillDown={canDrillDown}
        />

        {model.groups.length === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={TOTAL_COLUMNS} sx={{ textAlign: 'center', py: 5 }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          model.groups.map((group) => {
            const expanded = !collapsedGroups.has(group.key);
            const collapsible = group.rows.length > 1;

            return (
              <TableBody key={group.key} id={groupBodyId(group.key)}>
                <MatrixGroupRow
                  group={group}
                  expanded={expanded}
                  bodyId={groupBodyId(group.key)}
                  collapsible={collapsible}
                  canDrillDown={canDrillDown}
                  onToggle={onToggleGroup}
                  onSelect={onGroupSelect}
                />
                {expanded &&
                  group.rows.map((row) => (
                    <MatrixDataRow
                      key={row.key}
                      row={row}
                      columnMaxima={model.columnMaxima}
                      canDrillDown={canDrillDown}
                      isActive={isActive}
                      onKeyDown={handleKeyDown}
                      registerRef={registerRef}
                      onRowSelect={onRowSelect}
                      onCellSelect={onCellSelect}
                    />
                  ))}
              </TableBody>
            );
          })
        )}
      </Table>
    </TableContainer>
  );
};
